/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Interval';

const IntervalManager: ComponentManagerFn<'Interval'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'Interval'>(platform, 'Interval'),
    type: 'Interval',
    name: 'Interval',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnInterval'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      intervalInMs: {
        label: 'Interval In Milliseconds',
        type: 'numberInput',
        required: true,
      },
    },
    getDisplayText: (item) => `${item.props.intervalInMs}`,
    category: 'Dashboard',
    noOverlay: true,
    layout: 'auto',
    icon: { iconName: 'alarm-snooze' },
    description: `**Overview**<br/>
The Interval component is a logic-only controller that executes actions repeatedly based on a fixed time schedule. Unlike visual components (like Buttons or Charts), the Interval does not render any UI on the page. Instead, it acts as a background "heartbeat" for your application, automatically triggering workflows without requiring user interaction.
This component is essential for building dynamic, real-time interfaces, such as auto-refreshing dashboards, live status monitors, or polling systems.

**1. Configuration**<br/>
The Interval component requires a single, critical input to function.
* **Interval In Milliseconds:** Defines the frequency of the trigger. (Unit: Milliseconds, where 1000ms = 1 second).
* **Examples:**
    * 1000 = Every 1 second (High frequency).
    * 30000 = Every 30 seconds (Standard dashboard refresh).
    * 60000 = Every 1 minute (Low frequency background check).

**2. Logic & Triggers**<br/>
Because the Interval component has no visual presence, it relies entirely on child triggers to define what happens when the timer fires.
* **Allowed Child:** This component accepts only one specific child type: **TriggerOnInterval**.
* **Workflow:** The Page loads and the Interval timer starts. Every *X* milliseconds, the component fires, and the TriggerOnInterval child executes its attached logic.

**3. Common Use Cases**<br/>
* **Dashboard Auto-Refresh:** Reload a Data Store every 60 seconds to keep KPI metrics up to date.
* **Live Polling:** Check a backend service every 5 seconds to see if a long-running job (like a file export) has completed.
* **Session Management:** Periodically validate the user's session token in the background to prevent unexpected logouts.

**4. Best Practices**<br/>
* **Performance:** Be cautious with very low intervals (e.g., < 1000ms). Frequent triggers can overwhelm the browser or your backend API if the logic is complex.
* **Lifecycle:** The Interval runs as long as the page is active. It automatically stops when the user navigates away from the page.`,
  };
};

export default IntervalManager;
